<script setup>
/* eslint-disable @intlify/vue-i18n/no-raw-text, vue/no-bare-strings-in-template, vue/html-closing-bracket-newline */
import { computed, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
const props = defineProps({ offers: { type: Array, default: () => [] } });
const numeric = value => {
  const text = String(value ?? '').trim();
  return Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text
  );
};
const order = ref('amount_desc');
const expanded = ref(false);
const sorted = computed(() => {
  const [field, direction] = order.value.split('_');
  return [...props.offers].sort((a, b) => {
    const left = numeric(a[field]);
    const right = numeric(b[field]);
    const validLeft = Number.isFinite(left) && left > 0;
    const validRight = Number.isFinite(right) && right > 0;
    if (!validLeft || !validRight)
      return Number(validRight) - Number(validLeft);
    return direction === 'asc' ? left - right : right - left;
  });
});
const visible = computed(() =>
  expanded.value ? sorted.value : sorted.value.slice(0, 3)
);
watch(order, () => {
  expanded.value = false;
});
const currency = value =>
  numeric(value).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
const rate = value =>
  `${numeric(value).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
</script>

<template>
  <div class="space-y-4">
    <label
      class="flex flex-wrap items-center justify-between gap-2 text-sm text-n-slate-11"
    >
      <span>Ordenar ofertas</span>
      <Select
        v-model="order"
        aria-label="Ordenar ofertas"
        :options="[
          { value: 'amount_desc', label: 'Maior valor liberado' },
          { value: 'amount_asc', label: 'Menor valor liberado' },
          { value: 'term_desc', label: 'Prazo maior' },
          { value: 'term_asc', label: 'Prazo menor' },
        ]"
      />
    </label>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <article
        v-for="(offer, index) in visible"
        :key="index"
        class="min-w-0 space-y-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        data-testid="bank-offer"
      >
        <div
          class="flex flex-wrap justify-between gap-2 text-sm text-n-slate-11"
        >
          <span>Opção {{ index + 1 }}</span
          ><span v-if="offer.term">{{ offer.term }} parcelas</span>
        </div>
        <div>
          <span class="text-sm text-n-slate-11">Valor liberado</span
          ><strong class="block text-2xl text-n-teal-11">{{
            currency(offer.amount)
          }}</strong>
        </div>
        <dl class="space-y-2 border-t border-n-weak pt-3 text-sm">
          <div v-if="offer.installment" class="flex justify-between gap-2">
            <dt class="text-n-slate-11">Parcela</dt>
            <dd class="m-0">{{ currency(offer.installment) }}</dd>
          </div>
          <div v-if="offer.rate" class="flex justify-between gap-2">
            <dt class="text-n-slate-11">Taxa</dt>
            <dd class="m-0">{{ rate(offer.rate) }}</dd>
          </div>
          <div v-if="offer.table">
            <dt class="text-n-slate-11">Tabela</dt>
            <dd class="m-0 break-words">{{ offer.table }}</dd>
          </div>
        </dl>
      </article>
    </div>
    <div
      v-if="offers.length > 3"
      class="flex flex-wrap items-center justify-between gap-3 text-sm text-n-slate-11"
    >
      <span>{{ visible.length }} de {{ offers.length }} ofertas</span>
      <Button
        outline
        :label="expanded ? 'Ver menos' : 'Ver mais'"
        :aria-expanded="expanded"
        @click="expanded = !expanded"
      />
    </div>
  </div>
</template>
